/**
 * Scroll-scrub hero video for page-header-logo-banner (disave.si).
 * Canvas frame rendering + video seek fallback; optional particles.
 */
(function () {
  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function visibleVideo(root) {
    const mobile = root.querySelector(".wt-video__item--mobile-only video");
    const desktop = root.querySelector(".wt-video__item:not(.wt-video__item--mobile-only) video");
    if (window.matchMedia("(max-width: 899px)").matches && mobile) return mobile;
    return desktop || mobile || root.querySelector("video");
  }

  function hidePosters(root) {
    root.querySelectorAll(".wt-video__poster-img").forEach((img) => {
      img.style.opacity = "0";
    });
  }

  class ScrollHeroVideo {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector(".wt-scroll-hero__canvas");
      this.particlesCanvas = root.querySelector(".wt-scroll-hero__particles");
      this.host = root.closest("page-header-logo-banner");
      this.wrap = root.closest("[data-scroll-hero-wrap]");
      this.pin = this.wrap?.querySelector(".wt-scroll-hero-pin");
      this.additional = this.host?.querySelector(".wt-logo-banner__additional");
      this.ctx = this.canvas?.getContext("2d");
      this.pCtx = this.particlesCanvas?.getContext("2d");
      this.video = visibleVideo(root);
      this.frames = [];
      this.framesReady = false;
      this.lastFrameIndex = -1;
      this.videoSeeking = false;
      this.particles = [];
      this.enabled = !REDUCED_MOTION && !!this.canvas && !!this.video && !!this.ctx;
      this.scrubVh = parseFloat(root.dataset.scrubVh || "1.2");
      this.scrubSeconds = parseFloat(root.dataset.scrubSeconds || "0");
      this.smoothedProgress = 0;
      this.useParticles = root.dataset.particles === "true" && !!this.particlesCanvas;
      this.extractFrames = root.dataset.extractFrames === "true";
    }

    init() {
      if (!this.enabled) {
        this.root.classList.add("wt-scroll-hero--fallback");
        return;
      }

      this.root.classList.add("wt-scroll-hero--active");
      this.video.removeAttribute("autoplay");
      this.video.loop = false;
      this.video.pause();
      this.video.muted = true;
      this.video.playsInline = true;

      this.resizeCanvas();
      window.addEventListener("resize", () => {
        this.resizeCanvas();
        this.resizeParticles();
      });

      this.video.addEventListener("loadeddata", () => {
        this.video.currentTime = 0.001;
        hidePosters(this.root);
      });
      this.video.addEventListener("seeked", () => {
        this.videoSeeking = false;
        this.drawVideoFrame();
      });
      this.video.addEventListener("stalled", () => {
        this.videoSeeking = false;
      });

      if (this.useParticles) {
        this.particlesCanvas.hidden = false;
        this.resizeParticles();
        this.animateParticles();
      }

      window.addEventListener("scroll", this.onScroll.bind(this), { passive: true });
      this.onScroll();

      if (this.extractFrames) {
        this.extractFramesAsync();
      } else if (this.video.readyState >= 1) {
        hidePosters(this.root);
      }

      this.tick();
    }

    getProgress() {
      // Pinned način: progress temelji na tem, koliko wrapa smo prescrollali,
      // medtem ko hero stoji pripet (sticky) na vrhu zaslona.
      if (this.wrap && this.pin) {
        const scrollable = this.wrap.offsetHeight - this.pin.offsetHeight;
        if (scrollable > 0) {
          const top = this.wrap.getBoundingClientRect().top;
          return Math.max(0, Math.min(1, -top / scrollable));
        }
      }
      const end = window.innerHeight * this.scrubVh;
      if (end <= 0) return 0;
      return Math.max(0, Math.min(1, window.scrollY / end));
    }

    /* Del videa, ki se scrubba med scrollom (omejen z nastavitvijo "Del videa za scroll"). */
    getScrubDuration() {
      const full = this.video.duration;
      if (!full || !isFinite(full)) return 0;
      if (this.scrubSeconds > 0) return Math.min(this.scrubSeconds, full);
      return full;
    }

    resizeCanvas() {
      if (!this.canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
        this.lastFrameIndex = -1;
      }
    }

    drawVideoFrame() {
      if (!this.video || this.video.readyState < 2) return;
      const cw = this.canvas.width;
      const ch = this.canvas.height;
      const vw = this.video.videoWidth;
      const vh = this.video.videoHeight;
      if (!vw || !vh) return;
      const scale = Math.max(cw / vw, ch / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      this.ctx.drawImage(this.video, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }

    drawFrame(bitmap) {
      const cw = this.canvas.width;
      const ch = this.canvas.height;
      const scale = Math.max(cw / bitmap.width, ch / bitmap.height);
      const dw = bitmap.width * scale;
      const dh = bitmap.height * scale;
      this.ctx.drawImage(bitmap, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }

    async extractFramesAsync() {
      const src = this.video.currentSrc || this.video.src;
      if (!src) return;

      try {
        const response = await fetch(src);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const video = document.createElement("video");
        video.muted = true;
        video.playsInline = true;
        video.preload = "auto";
        video.src = objectUrl;

        await new Promise((resolve, reject) => {
          video.onloadedmetadata = () => resolve();
          video.onerror = () => reject(new Error("metadata"));
          setTimeout(() => reject(new Error("timeout")), 15000);
        });

        const scale = Math.min(1, 1280 / video.videoWidth);
        const scaledWidth = Math.round(video.videoWidth * scale);
        const scaledHeight = Math.round(video.videoHeight * scale);
        const scrubDuration =
          this.scrubSeconds > 0 ? Math.min(this.scrubSeconds, video.duration) : video.duration;
        const frameCount = Math.max(24, Math.min(90, Math.round(scrubDuration * 20)));

        for (let i = 0; i < frameCount; i++) {
          const time = (i / (frameCount - 1)) * Math.max(0, scrubDuration - 0.05);
          video.currentTime = time;
          await new Promise((resolve, reject) => {
            const onSeeked = () => {
              video.removeEventListener("seeked", onSeeked);
              resolve();
            };
            video.addEventListener("seeked", onSeeked);
            setTimeout(() => {
              video.removeEventListener("seeked", onSeeked);
              reject(new Error("seek"));
            }, 3000);
          });
          const bitmap = await createImageBitmap(video, {
            resizeWidth: scaledWidth,
            resizeHeight: scaledHeight,
          });
          this.frames.push(bitmap);
        }

        if (this.frames.length) {
          this.framesReady = true;
          hidePosters(this.root);
        }
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        /* seek fallback */
      }
    }

    onScroll() {
      if (!this.additional) return;
      const fade = Math.max(0, 1 - window.scrollY / (window.innerHeight * 0.32));
      this.additional.style.opacity = String(fade);
      this.additional.style.pointerEvents = fade < 0.1 ? "none" : "";
    }

    tick() {
      if (!this.enabled) return;
      const raw = this.getProgress();
      // Glajenje: currentTime sledi scrollu z zamikom, da video ne skače.
      this.smoothedProgress += (raw - this.smoothedProgress) * 0.14;
      if (Math.abs(raw - this.smoothedProgress) < 0.0005) this.smoothedProgress = raw;
      const progress = this.smoothedProgress;

      if (this.framesReady && this.frames.length) {
        const idx = Math.round(progress * (this.frames.length - 1));
        if (idx !== this.lastFrameIndex && this.frames[idx]) {
          this.lastFrameIndex = idx;
          this.drawFrame(this.frames[idx]);
        }
      } else if (this.video.readyState >= 1) {
        const scrubDuration = this.getScrubDuration();
        if (scrubDuration > 0) {
          const target = progress * scrubDuration;
          if (!this.videoSeeking && Math.abs(this.video.currentTime - target) > 0.04) {
            this.videoSeeking = true;
            this.video.currentTime = target;
          }
        }
      }

      requestAnimationFrame(this.tick.bind(this));
    }

    resizeParticles() {
      if (!this.useParticles || !this.particlesCanvas) return;
      this.particlesCanvas.width = window.innerWidth;
      this.particlesCanvas.height = window.innerHeight;
      this.particles = [];
      const count = Math.floor((this.particlesCanvas.width * this.particlesCanvas.height) / 14000);
      for (let i = 0; i < count; i++) {
        this.particles.push({
          x: Math.random() * this.particlesCanvas.width,
          y: Math.random() * this.particlesCanvas.height,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25,
          size: Math.random() * 1.4 + 0.4,
          opacity: Math.random() * 0.35 + 0.08,
        });
      }
    }

    animateParticles() {
      if (!this.useParticles || !this.pCtx || !this.particlesCanvas) return;
      this.pCtx.clearRect(0, 0, this.particlesCanvas.width, this.particlesCanvas.height);
      for (const p of this.particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = this.particlesCanvas.width;
        if (p.x > this.particlesCanvas.width) p.x = 0;
        if (p.y < 0) p.y = this.particlesCanvas.height;
        if (p.y > this.particlesCanvas.height) p.y = 0;
        this.pCtx.beginPath();
        this.pCtx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        this.pCtx.fillStyle = `rgba(255,255,255,${p.opacity})`;
        this.pCtx.fill();
      }
      requestAnimationFrame(this.animateParticles.bind(this));
    }
  }

  function init(root) {
    if (root.dataset.scrollHeroReady === "1") return;
    root.dataset.scrollHeroReady = "1";
    const instance = new ScrollHeroVideo(root);
    instance.init();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-scroll-hero]").forEach(init);
  }

  if (document.readyState !== "loading") initAll();
  else document.addEventListener("DOMContentLoaded", () => initAll());

  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();
